import { coerceNumberProperty } from '@angular/cdk/coercion';
import { CdkVirtualScrollViewport, VIRTUAL_SCROLL_STRATEGY, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import {
    computed,
    DestroyRef,
    Directive,
    ElementRef,
    forwardRef,
    inject,
    input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalService } from '@df/ui/modal/modal.service';
import { Observable, Observer, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export class ItemDimension {
    columns = 0;
    height = 0;
}

// Only support vertical scroll
export class MultiColumnVirtualScrollStrategy implements VirtualScrollStrategy {
    offset: number = 0;

    private readonly _scrolledIndexChange = new Subject<number>();
    private readonly _renderedOffsetChanged = new Subject<number>();

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    scrolledIndexChange: Observable<number> = this._scrolledIndexChange.pipe(distinctUntilChanged());
    renderedOffsetChanged: Observable<number> = this._renderedOffsetChanged.pipe(distinctUntilChanged());

    private _itemDimension: ItemDimension;
    private _minBufferPx: number;
    private _maxBufferPx: number;
    private _viewport: CdkVirtualScrollViewport | null = null;

    constructor(columns: number, height: number, minBufferPx: number, maxBufferPx: number) {
        this._itemDimension = { columns, height };
        this._minBufferPx = minBufferPx;
        this._maxBufferPx = maxBufferPx;
    }

    /**
     * after viewPort size calculated, attach to the viewPort
     * @param viewport
     */
    attach(viewport: CdkVirtualScrollViewport): void {
        this._viewport = viewport;
        this._updateTotalContentSize();
        this._updateRenderedRange();
    }

    /**
     * detach on viewPort destroy
     */
    detach(): void {
        this._scrolledIndexChange.complete();
        this._renderedOffsetChanged.complete();
        this._viewport = null;
    }

    /**
     * Update the item size and buffer size.
     * @param itemSize The size of the items in the virtually scrolling list.
     * @param minBufferPx The minimum amount of buffer (in pixels) before needing to render more
     * @param maxBufferPx The amount of buffer (in pixels) to render when rendering more.
     */
    updateItemAndBufferSize(columns: number, height: number, minBufferPx: number, maxBufferPx: number) {
        if (maxBufferPx < minBufferPx) {
            throw Error('CDK virtual scroll: maxBufferPx must be greater than or equal to minBufferPx');
        }
        this._itemDimension = { columns, height };
        this._minBufferPx = minBufferPx;
        this._maxBufferPx = maxBufferPx;
        this._updateTotalContentSize();
        this._updateRenderedRange();
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onContentScrolled(): void {
        this._updateRenderedRange();
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onDataLengthChanged(): void {
        this._updateTotalContentSize();
        this._updateRenderedRange();
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onContentRendered(): void {
        /* no-op */
    }

    /** @docs-private Implemented as part of VirtualScrollStrategy. */
    onRenderedOffsetChanged(): void {
        this._renderedOffsetChanged.next(this.offset);
    }

    /**
     * scroll to item by index
     * @param dataIndex index of the data item
     * @param behavior scroll behavior
     */
    scrollToIndex(dataIndex: number, behavior: ScrollBehavior): void {
        if (this._viewport) {
            this._viewport.scrollToOffset(this.getScrollIndex(dataIndex) * this._itemDimension.height, behavior);
        }
    }
    /**
     *
     * @param dataIndex of the data
     * @returns offset of the index
     */
    private getScrollIndex(dataIndex: number) {
        if (this._viewport) {
            if (this._viewport.orientation === 'vertical') {
                const colPerRow = this.getColPerRow();
                const rowIndex = Math.floor(dataIndex / colPerRow);
                return rowIndex;
            } else {
                console.warn('The horizontal mode is not support yet.');
            }
        }
        return 0;
    }

    /**
     *
     * @returns range of data to render
     */
    private _updateRenderedRange() {
        if (!this._viewport) {
            return;
        }
        const colPerRow = this.getColPerRow();
        // get the current rendered range {start,end} of the data, actually the start/end indexs of the array
        const renderedRange = this._viewport.getRenderedRange();
        // init new render range as current, start, end
        const newRange = { start: renderedRange.start, end: renderedRange.end };
        // actually the height of the view port, we calculated based on height
        const viewportSize = this._viewport.getViewportSize();

        // total data length
        const dataLength = this._viewport.getDataLength();
        // current scrolloffset
        let scrollOffset = this._viewport.measureScrollOffset();

        // Prevent NaN as result when dividing by zero.
        const itemSize = this._itemDimension.height;
        // Totally same as fixed size scrolling start here except dataLength->rowLength
        let firstVisibleIndex = itemSize > 0 ? scrollOffset / itemSize : 0;

        // If user scrolls to the bottom of the list and data changes to a smaller list
        // use original range to check exceed condition
        if (newRange.end > dataLength) {
            // We have to recalculate the first visible index based on new data length and viewport size.
            const maxVisibleItems = Math.ceil(viewportSize / itemSize);
            const newVisibleIndex = Math.max(0, Math.min(firstVisibleIndex, dataLength - maxVisibleItems)) * colPerRow;

            // If first visible index changed we must update scroll offset to handle start/end buffers
            // Current range must also be adjusted to cover the new position (bottom of new list).
            if (firstVisibleIndex != newVisibleIndex) {
                firstVisibleIndex = newVisibleIndex;
                scrollOffset = newVisibleIndex * itemSize;
                newRange.start = Math.floor(firstVisibleIndex) * colPerRow;
            }

            newRange.end = Math.max(0, Math.min(dataLength, (newRange.start + maxVisibleItems) * colPerRow));
        }

        const rowRange = { start: newRange.start / colPerRow, end: Math.ceil(newRange.end / colPerRow) };
        const startBuffer = scrollOffset - rowRange.start * itemSize;

        if (startBuffer < this._minBufferPx && rowRange.start != 0) {
            const expandStart = Math.ceil((this._maxBufferPx - startBuffer) / itemSize);

            rowRange.start = Math.max(0, rowRange.start - expandStart);
            rowRange.end = Math.min(dataLength, Math.ceil(firstVisibleIndex + (viewportSize + this._minBufferPx) / itemSize));
        } else {
            const endBuffer = rowRange.end * itemSize - (scrollOffset + viewportSize);

            if (endBuffer < this._minBufferPx && rowRange.end != dataLength) {
                const expandEnd = Math.ceil((this._maxBufferPx - endBuffer) / itemSize);

                if (expandEnd > 0) {
                    rowRange.end = Math.min(dataLength, rowRange.end + expandEnd);
                    rowRange.start = Math.max(0, Math.floor(firstVisibleIndex - this._minBufferPx / itemSize));
                }
            }
        }
        const updatedRange = {
            start: rowRange.start * colPerRow,
            end: Math.min(dataLength, rowRange.end * colPerRow)
        };
        // Totally same as fixed size scrolling above
        this._viewport.setRenderedRange(updatedRange);
        this.offset = this._itemDimension.height * rowRange.start;
        this._viewport.setRenderedContentOffset(this.offset);
        this._scrolledIndexChange.next(Math.floor(firstVisibleIndex));
    }

    private getColPerRow() {
        return coerceNumberProperty(this._itemDimension.columns);
    }

    /**
     *
     * @returns Total virtual scroll size based on datalength and itemDemension
     */
    private _updateTotalContentSize() {
        if (!this._viewport) {
            return;
        }
        const colPerRow = this.getColPerRow();
        const rows = Math.ceil(this._viewport.getDataLength() / colPerRow);
        this._viewport.setTotalContentSize(rows * this._itemDimension.height);
    }
}

/**
 * Provider factory for `MultiColumnsVirtualScrollStrategy` that simply extracts the already created
 * `MultiColumnsVirtualScrollStrategy` from the given directive.
 * @param multiColumnsDir The instance of `CdkMultiColumnsVirtualScroll` to extract the
 *     `MultiColumnsVirtualScrollStrategy` from.
 */
export function _multiColumnVirtualScrollStrategyFactory(multiColumnsDir: CdkMultiColumnsVirtualScrollDirective) {
    return multiColumnsDir._scrollStrategy;
}

/** A virtual scroll strategy that supports multi-column items. */
@Directive({
    selector: 'cdk-virtual-scroll-viewport[columns]',
    host: {
        '[style.--cdk-virtual-scroll-viewport-item-height.px]': 'height()',
        '[style.--cdk-virtual-scroll-viewport-item-offset.px]': 'offset()'
    },
    standalone: true,
    providers: [
        {
            provide: VIRTUAL_SCROLL_STRATEGY,
            useFactory: _multiColumnVirtualScrollStrategyFactory,
            deps: [forwardRef(() => CdkMultiColumnsVirtualScrollDirective)]
        }
    ]
})
export class CdkMultiColumnsVirtualScrollDirective implements OnChanges, OnInit {
    columns = input<number>(1);
    height = input<number>(360);
    minBuffer = input<number, number>(2, { transform: coerceNumberProperty });
    maxBuffer = input<number, number>(3, { transform: coerceNumberProperty });
    offset = signal<number>(0);

    /**
     * The minimum amount of buffer rendered beyond the viewport (in pixels).
     * If the amount of buffer dips below this number, more items will be rendered.
     */
    minBufferPx = computed(() => this.minBuffer() * this.height());

    /**
     * The number of pixels worth of buffer to render for when rendering new items.
     */
    maxBufferPx = computed(() => this.maxBuffer() * this.height());

    @Output()
    readonly renderedOffsetChanged = new Observable<number>((observer: Observer<number>) =>
        this._scrollStrategy.renderedOffsetChanged.subscribe(offset =>
            Promise.resolve().then(() => this.ngZone.run(() => observer.next(offset)))
        )
    );

    /** The scroll strategy used by this directive. */
    _scrollStrategy = new MultiColumnVirtualScrollStrategy(this.columns(), this.height(), this.minBufferPx(), this.maxBufferPx());

    protected ngZone = inject(NgZone);
    protected modalService = inject(ModalService);
    protected destroyRef = inject(DestroyRef);
    protected renderer2 = inject(Renderer2);
    protected elementRef = inject(ElementRef);

    ngOnInit() {
        this.modalService.afterOpened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.offset.set(this._scrollStrategy.offset);
        });
    }

    ngOnChanges() {
        this._scrollStrategy.updateItemAndBufferSize(this.columns(), this.height(), this.minBufferPx(), this.maxBufferPx());
    }
}
